import { useEffect, useRef, useState } from "react";
import { useAiChat } from "@/hooks/useAiChat";
import { Sparkles, Send, ExternalLink, Wand2 } from "lucide-react";
import AiIntakeWorkspace from "@/pages/AiIntakeWorkspace";

const SUGGESTIONS = [
  "Summarise our relationship with Airbus",
  "Which projects are currently in production?",
  "What's the status of our most recent quote?",
];

const TABS = [
  { key: "ask", label: "Ask", icon: Sparkles },
  { key: "intake", label: "Project Intake", icon: Wand2 },
];

export default function AiAssistant() {
  const [tab, setTab] = useState("ask");
  const { messages, prompt, setPrompt, send, streaming, status, provider, contextRecords } = useAiChat();
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  return (
    <div className="flex h-full flex-col p-8 pb-20" data-testid="ai-assistant-page">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-6 w-6 text-[#B45309]" />
        <h1 className="font-editorial text-4xl tracking-tight">AI Assistant</h1>
        {tab === "ask" && <span className="rounded bg-[#FFC817]/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-[#B45309]">read-only</span>}
      </div>
      <p className="text-sm text-[#A8A29E] mb-4">
        {tab === "ask" ? "Ask about a client, project, or contact. Answers cite records you have access to." : "Paste a raw client brief and get a structured, editable Project Brief recommendation — nothing is priced or saved until you confirm."}
      </p>

      <div className="flex gap-1.5 mb-4" data-testid="ai-assistant-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} data-testid={`ai-assistant-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${tab === t.key ? "bg-[#1C1C1E] text-white" : "border border-[#E7E5E4] text-[#57534E] hover:border-[#FFC817]"}`}>
              <Icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "intake" ? (
        <AiIntakeWorkspace onExit={() => setTab("ask")} />
      ) : (
      <div className="flex-1 min-h-0 rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] flex flex-col">
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid={`ai-suggestion-${s.slice(0, 10).replace(/\s/g, "-")}`}
                  onClick={() => setPrompt(s)}
                  className="rounded-full border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817]/40 hover:text-[#B45309]">
                  {s}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "ml-auto max-w-[70%] rounded-lg rounded-tr-none bg-[#E7E5E4] p-3 text-sm text-[#1C1C1E]" : "max-w-[85%] rounded-lg rounded-tl-none border border-[#E7E5E4] p-3 text-sm text-[#57534E] whitespace-pre-wrap"}>
              {m.text || (streaming && i === messages.length - 1 ? <span className="text-[#A8A29E]" data-testid="ai-page-status">{status || "thinking…"}</span> : null)}
            </div>
          ))}
          {contextRecords.length > 0 && !streaming && (
            <div className="flex flex-wrap gap-1.5" data-testid="ai-page-citations">
              {contextRecords.slice(0, 8).map((r) => (
                <span key={r.id} className="inline-flex items-center gap-1 rounded border border-[#D6D3D1] bg-[#F5F5F4] px-1.5 py-0.5 text-[10px] text-[#57534E]">
                  <ExternalLink className="h-2.5 w-2.5" /> {r.type}:{(r.name || r.id).slice(0, 24)}
                </span>
              ))}
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="border-t border-[#E7E5E4] p-4">
          {provider && <div className="mb-2 text-[10px] uppercase tracking-wider font-mono text-[#A8A29E]">via {provider}</div>}
          <div className="flex gap-2">
            <input
              data-testid="ai-page-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send({}); }}
              placeholder={streaming ? "Streaming…" : "Ask the assistant…"}
              disabled={streaming}
              className="flex-1 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2.5 text-sm text-[#1C1C1E] placeholder-[#A8A29E] focus:border-[#FFC817] outline-none"
            />
            <button
              data-testid="ai-page-send"
              onClick={() => send({})} disabled={streaming || !prompt.trim()}
              className="rounded-md bg-[#FFC817] px-4 py-2.5 text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
